// `/api/v1/consent/*`: a run's request to read one resource's contents, and the
// workspace's answer (#263, C6).
//
// Read off `control_plane/routes/consent.py`. `GET …/requests` answers the
// workspace's unanswered requests, `GET …/grants` its standing grants, and
// `POST …/requests/{id}` takes the answer.
//
// **The answer is one word.** The route forbids every key but the answer and its
// step-up proof, and this module sends nothing else: no resource, no operation,
// no scope, because the grant is written from the request row the run's own
// request wrote (#17 round 2 Q3, "server-constructed").
//
// **A standing grant is two calls**, in C3's shape (`api/policy.ts`,
// `revisePolicy`): `POST /auth/step-up` for this purpose mints the grant, and the
// grant rides the answer's body as `step_up_grant`. `run` and `declined` need
// none (#17 round 2 Q4).

import type { Api } from "./http";
import { STEP_UP_PATH, type StepUp } from "./policy";

export const CONSENT_REQUESTS_PATH = "/api/v1/consent/requests";
export const CONSENT_GRANTS_PATH = "/api/v1/consent/grants";
/** The purpose `routes/consent.py::STEP_UP_PURPOSE` asks the grant to name. */
export const CONSENT_STEP_UP_PURPOSE = "egress_grant";

export const consentAnswerPath = (requestId: string): string =>
  `${CONSENT_REQUESTS_PATH}/${encodeURIComponent(requestId)}`;

/** The three answers, in the order the screen offers them. */
export type ConsentAnswer = "run" | "always" | "declined";
export const CONSENT_ANSWERS: readonly ConsentAnswer[] = ["run", "always", "declined"];

/** What each answer is called on the screen. */
export const ANSWER_LABELS: Record<ConsentAnswer, string> = {
  run: "Allow for this investigation",
  always: "Always allow this resource",
  declined: "Decline",
};

/** One unanswered request: what the run asked to read, why, and until when. */
export interface ConsentRequestRow {
  /** Server-derived authority; missing means no permission. */
  may_answer?: boolean;
  request_id: string;
  run_id: string;
  resource_arn: string;
  operations: string[];
  reason: string;
  requested_at: string;
  /** Unanswered by then, the run goes on without the contents (#17 round 3 Q6). */
  expires_at: string;
}

/** One standing grant: what every later run of the workspace may read. */
export interface StandingGrantRow {
  grant_id: string;
  resource_arn: string;
  operations: string[];
  granted_at: string;
}

export interface ConsentRead {
  requests: ConsentRequestRow[];
  grants: StandingGrantRow[];
}

/** What the answer did: `granted`, `declined`, `dropped` or `applies_to_next_run`. */
export interface ConsentOutcome {
  outcome: string;
  grant_id: string | null;
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

function asRequest(value: unknown): ConsentRequestRow | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.request_id !== "string" || typeof row.resource_arn !== "string") return null;
  return {
    request_id: row.request_id,
    may_answer: row.may_answer === true,
    run_id: text(row.run_id),
    resource_arn: row.resource_arn,
    operations: strings(row.operations),
    reason: text(row.reason),
    requested_at: text(row.requested_at),
    expires_at: text(row.expires_at),
  };
}

function asGrant(value: unknown): StandingGrantRow | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.grant_id !== "string" || typeof row.resource_arn !== "string") return null;
  return {
    grant_id: row.grant_id,
    resource_arn: row.resource_arn,
    operations: strings(row.operations),
    granted_at: text(row.granted_at),
  };
}

export function consentFrom(requests: unknown, grants: unknown): ConsentRead {
  return {
    requests: Array.isArray(requests)
      ? requests.map(asRequest).filter((r): r is ConsentRequestRow => r !== null)
      : [],
    grants: Array.isArray(grants)
      ? grants.map(asGrant).filter((g): g is StandingGrantRow => g !== null)
      : [],
  };
}

export async function readConsent(api: Api): Promise<ConsentRead> {
  const [requests, grants] = await Promise.all([
    api.get<unknown>(CONSENT_REQUESTS_PATH),
    api.get<unknown>(CONSENT_GRANTS_PATH),
  ]);
  return consentFrom(requests, grants);
}

/**
 * Answer one request. `always` steps up for this purpose first and sends the
 * grant beside the answer; the other two send the answer alone.
 */
export async function answerConsent(
  api: Api,
  requestId: string,
  answer: ConsentAnswer,
  stepUp: StepUp | null,
): Promise<ConsentOutcome> {
  const body: { answer: ConsentAnswer; step_up_grant?: string } = { answer };
  if (answer === "always") {
    if (stepUp === null || stepUp.credential === "") {
      throw new Error("Always allowing a resource needs you to confirm it is you.");
    }
    const granted = await api.post<{ grant?: unknown }>(STEP_UP_PATH, {
      method: stepUp.method,
      credential: stepUp.credential,
      purpose: CONSENT_STEP_UP_PURPOSE,
    });
    body.step_up_grant = text(granted?.grant);
  }
  const answered = await api.post<{ outcome?: unknown; grant_id?: unknown }>(
    consentAnswerPath(requestId),
    body,
  );
  return {
    outcome: text(answered?.outcome),
    grant_id: typeof answered?.grant_id === "string" ? answered.grant_id : null,
  };
}

/** The workspace's opt-in to its runs asking for consent (`routes/consent.py`, #1453). */
export const CONSENT_POLICY_PATH = "/api/v1/consent/policy";

/** The longest consent wait the product offers (one day); the server refuses more. */
export const MAX_TIMEOUT_SECONDS = 86_400;

/** `enabled` false is no row at all; `timeout_seconds` null is the platform's default. */
export interface ConsentPolicy {
  enabled: boolean;
  timeout_seconds: number | null;
}

/** The permission that answers a consent request, and so changes this (`store/consent.py`). */
export const CONSENT_POLICY_WRITE_PERMISSION = "investigations:manage";

const asPolicy = (value: unknown): ConsentPolicy => {
  const row = (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;
  return {
    enabled: row.enabled === true,
    timeout_seconds: typeof row.timeout_seconds === "number" ? row.timeout_seconds : null,
  };
};

export async function readConsentPolicy(api: Api): Promise<ConsentPolicy> {
  return asPolicy(await api.get<unknown>(CONSENT_POLICY_PATH));
}

export async function saveConsentPolicy(api: Api, policy: ConsentPolicy): Promise<ConsentPolicy> {
  return asPolicy(await api.put<unknown>(CONSENT_POLICY_PATH, policy));
}
